import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import Input from 'components/ui/Input';

export default function DatabaseConnections() {
  const [connections, setConnections] = useState([
    {
      id: 1,
      name: 'ChemDATA Pro API',
      type: 'REST API',
      status: 'active',
      lastSync: '2 hours ago',
      records: 4523,
    },
    {
      id: 2,
      name: 'MatWeb Database',
      type: 'SQL Connection',
      status: 'active',
      lastSync: '1 day ago',
      records: 2341,
    },
    {
      id: 3,
      name: 'NIST Chemistry WebBook',
      type: 'Web Scraping',
      status: 'inactive',
      lastSync: '5 days ago',
      records: 892,
    },
  ]);

  const [showAddModal, setShowAddModal] = useState(false);

  const availableDatabases = [
    {
      name: 'Cole-Parmer Compatibility',
      type: 'REST API',
      icon: 'Database',
      description: 'Chemical resistance database for plastics and elastomers',
    },
    {
      name: 'CROW Database',
      type: 'SQL',
      icon: 'Server',
      description: 'Comprehensive chemical resistance data',
    },
    {
      name: 'Plastics International',
      type: 'API',
      icon: 'Cloud',
      description: 'Material compatibility and performance data',
    },
    {
      name: 'Custom Database',
      type: 'Custom',
      icon: 'Settings',
      description: 'Connect your own database',
    },
  ];

  const getStatusColor = (status) => {
    return status === 'active' ? 'text-green-500 bg-green-500/10' : 'text-gray-500 bg-gray-500/10';
  };

  return (
    <div className="space-y-6">
      {/* Active Connections */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Active Database Connections</h3>
            <p className="text-sm text-muted-foreground">Manage your external data sources</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-primary flex items-center gap-2"
          >
            <Icon name="Plus" size={18} />
            Add Connection
          </button>
        </div>

        <div className="space-y-3">
          {connections?.map((conn) => (
            <div key={conn?.id} className="p-4 border border-border rounded-lg hover:border-primary transition-colors">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-3 flex-1">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Icon name="Database" size={20} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-foreground">{conn?.name}</h4>
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(conn?.status)}`}>
                        {conn?.status}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">{conn?.type}</p>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Icon name="Clock" size={14} />
                        Last sync: {conn?.lastSync}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="FileText" size={14} />
                        {conn?.records} records
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button className="btn-secondary text-sm py-2 px-3">
                    <Icon name="RefreshCw" size={14} className="mr-1" />
                    Sync
                  </button>
                  <button className="btn-secondary text-sm py-2 px-3">
                    <Icon name="Settings" size={14} />
                  </button>
                </div>
              </div>
              <div className="h-2 bg-border rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all"
                  style={{ width: conn?.status === 'active' ? '100%' : '0%' }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Available Databases */}
      <div className="card">
        <h3 className="text-lg font-semibold text-foreground mb-4">Available Databases</h3>
        <div className="grid md:grid-cols-2 gap-4">
          {availableDatabases?.map((db, index) => (
            <div key={index} className="p-4 border border-border rounded-lg hover:border-primary transition-colors">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-accent rounded-lg">
                  <Icon name={db?.icon} size={20} className="text-primary" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-foreground mb-1">{db?.name}</h4>
                  <p className="text-sm text-muted-foreground mb-2">{db?.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{db?.type}</span>
                    <button className="text-sm text-primary hover:underline">Connect</button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Connection Statistics */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/10 rounded-lg">
              <Icon name="Link" size={20} className="text-green-500" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">3</div>
              <div className="text-sm text-muted-foreground">Active Connections</div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/10 rounded-lg">
              <Icon name="Database" size={20} className="text-blue-500" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">7,756</div>
              <div className="text-sm text-muted-foreground">Total Records</div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name="RefreshCw" size={20} className="text-primary" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">Daily</div>
              <div className="text-sm text-muted-foreground">Sync Frequency</div>
            </div>
          </div>
        </div>
      </div>
      {/* Add Connection Modal (simplified) */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Add New Connection</h3>
              <button onClick={() => setShowAddModal(false)}>
                <Icon name="X" size={20} className="text-muted-foreground" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Connection Name
                </label>
                <Input placeholder="Enter connection name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Database Type
                </label>
                <select className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground">
                  <option>REST API</option>
                  <option>SQL Database</option>
                  <option>Web Scraping</option>
                  <option>Custom</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Connection URL
                </label>
                <Input placeholder="https://api.example.com" />
              </div>
              <div className="flex gap-2 pt-4">
                <button onClick={() => setShowAddModal(false)} className="btn-secondary flex-1">
                  Cancel
                </button>
                <button className="btn-primary flex-1">Connect</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}